import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { User, Key } from 'lucide-react'
import { createPlayer, loginPlayer } from '../utils/api'
import { useGame } from '../context/GameContext'

export default function Welcome() {
  const navigate = useNavigate()
  const { hydrateProgressFromPlayerData } = useGame()
  const [mode, setMode] = useState<'choose' | 'guest' | 'password'>('choose')
  const [nickname, setNickname] = useState('')
  const [passwordInput, setPasswordInput] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const currentPw = localStorage.getItem('hsk2-current-player')

  const handleGuestLogin = async () => {
    if (!nickname.trim()) { setError('Please enter a nickname'); return }
    setError('')
    setLoading(true)
    try {
      const player = await createPlayer(nickname.trim())
      localStorage.setItem('hsk2-current-player', player.password)
      localStorage.setItem('hsk2-player-name', player.name)
      hydrateProgressFromPlayerData({
        totalScore: player.totalScore,
        bestStreak: player.bestStreak,
        wordsLearned: player.wordsLearned,
        sessionsPlayed: player.sessionsPlayed,
      })
      navigate('/')
    } catch (e: any) {
      setError(e.message || 'Failed to create guest account')
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordLogin = async () => {
    const pw = passwordInput.trim()
    if (!pw) { setError('Please enter your password'); return }
    setError('')
    setLoading(true)
    try {
      const player = await loginPlayer(pw)
      localStorage.setItem('hsk2-current-player', pw)
      localStorage.setItem('hsk2-player-name', player.name)
      hydrateProgressFromPlayerData({
        totalScore: player.totalScore,
        bestStreak: player.bestStreak,
        wordsLearned: player.wordsLearned,
        sessionsPlayed: player.sessionsPlayed,
      })
      navigate('/')
    } catch {
      setError('Password not found')
    } finally {
      setLoading(false)
    }
  }


  const handleContinueExisting = () => {
    if (currentPw) navigate('/')
  }

  return (
    <div className="page-shell bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 flex flex-col items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo / Title */}
        <div className="text-center mb-8">
          <div className="text-5xl md:text-6xl mb-3">🇨🇳</div>
          <h1 className="text-3xl md:text-5xl font-bold text-white drop-shadow-lg mb-2">HSK2 Game</h1>
          <p className="chinese-text text-lg md:text-xl text-white/80">汉语水平考试游戏</p>
        </div>

        {/* Continue as existing player */}
        {currentPw && mode === 'choose' && (
          <button
            onClick={handleContinueExisting}
            className="w-full bg-green-500 hover:bg-green-600 text-white rounded-2xl p-4 mb-4 shadow-xl
                       active:scale-95 transition-all flex items-center justify-center gap-3"
          >
            <User className="w-6 h-6 shrink-0" />
            <div className="text-left min-w-0">
              <p className="font-bold text-base md:text-lg truncate">Continue as {localStorage.getItem('hsk2-player-name') || 'Player'}</p>
              <p className="text-white/80 text-sm">Resume playing</p>
            </div>
          </button>
        )}

        {/* Choose mode */}
        {mode === 'choose' && (
          <div className="space-y-3">
            {/* Guest Login */}
            <button
              onClick={() => { setMode('guest'); setError('') }}
              className="w-full bg-white/20 backdrop-blur-lg hover:bg-white/30 text-white rounded-2xl p-4 shadow-xl
                         active:scale-95 transition-all flex items-center gap-4"
            >
              <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <User className="w-6 h-6 text-white" />
              </div>
              <div className="text-left flex-1">
                <p className="font-bold text-lg">Guest Login</p>
                <p className="text-white/70 text-sm">Play as a guest with a nickname</p>
              </div>
            </button>

            {/* Continue with Password */}
            <button
              onClick={() => { setMode('password'); setError('') }}
              className="w-full bg-white/20 backdrop-blur-lg hover:bg-white/30 text-white rounded-2xl p-4 shadow-xl
                         active:scale-95 transition-all flex items-center gap-4"
            >
              <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Key className="w-6 h-6 text-white" />
              </div>
              <div className="text-left flex-1">
                <p className="font-bold text-lg">Continue with Password</p>
                <p className="text-white/70 text-sm">Enter your password to restore progress</p>
              </div>
            </button>

            {/* Google Login - temporarily hidden */}
            {/* <GoogleSignIn
              onSuccess={handleGoogleLogin}
              onError={(msg) => setError(msg)}
            /> */}

            {error && <p className="text-red-200 text-sm mt-2 text-center">{error}</p>}
            {loading && <p className="text-white/60 text-sm text-center">Loading...</p>}
          </div>
        )}

        {/* Guest Login Form */}
        {mode === 'guest' && (
          <div className="bg-white/20 backdrop-blur-lg rounded-3xl p-6 shadow-xl">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-6 h-6 text-white" />
              <h2 className="text-xl font-bold text-white">Guest Login</h2>
            </div>
            <p className="text-white/70 text-sm mb-4">Enter your nickname</p>
            <input
              type="text"
              placeholder="Nickname..."
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleGuestLogin()}
              className="w-full rounded-2xl px-4 py-3 text-lg bg-white/90 text-gray-800
                         placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 mb-4"
              maxLength={20}
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={handleGuestLogin}
                disabled={!nickname.trim() || loading}
                className="flex-1 bg-green-500 text-white rounded-2xl px-6 py-3 font-bold
                           active:scale-95 transition-transform shadow-xl disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Start Playing!'}
              </button>
              <button
                onClick={() => { setMode('choose'); setError('') }}
                className="bg-white/20 text-white rounded-2xl px-4 py-3 font-bold
                           active:scale-95 transition-transform"
              >
                Back
              </button>
            </div>
            {error && <p className="text-red-200 text-sm mt-3">{error}</p>}
          </div>
        )}

        {/* Password Login Form */}
        {mode === 'password' && (
          <div className="bg-white/20 backdrop-blur-lg rounded-3xl p-6 shadow-xl">
            <div className="flex items-center gap-2 mb-4">
              <Key className="w-6 h-6 text-yellow-300" />
              <h2 className="text-xl font-bold text-white">Continue with Password</h2>
            </div>
            <p className="text-white/70 text-sm mb-4">Enter your 8-character password</p>
            <input
              type="text"
              placeholder="Password..."
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value.slice(0, 8))}
              onKeyDown={(e) => e.key === 'Enter' && handlePasswordLogin()}
              className="w-full rounded-2xl px-4 py-3 text-lg bg-white/90 text-gray-800
                         placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400
                         font-mono tracking-wider mb-4"
              maxLength={8}
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={handlePasswordLogin}
                disabled={!passwordInput.trim() || loading}
                className="flex-1 bg-amber-500 text-white rounded-2xl px-6 py-3 font-bold
                           active:scale-95 transition-transform shadow-xl disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Restore Progress'}
              </button>
              <button
                onClick={() => { setMode('choose'); setError('') }}
                className="bg-white/20 text-white rounded-2xl px-4 py-3 font-bold
                           active:scale-95 transition-transform"
              >
                Back
              </button>
            </div>
            {error && <p className="text-red-200 text-sm mt-3">{error}</p>}
          </div>
        )}
      </div>
    </div>
  )
}
